import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { usePremium } from "@/contexts/PremiumContext";
import { useLanguage } from "@/i18n/LanguageProvider";
import { useAvatar } from "@/hooks/useAvatar";
import { capturePageview, setPersonProperties } from "@/lib/analytics";
import { metaPageView } from "@/lib/metaPixel";
import { metricoolPageView } from "@/lib/metricool";

/**
 * Bridges router + app state into PostHog. Mounted once inside the provider
 * tree and the router:
 *   1. Manual $pageview on every SPA route change (autocapture pageview is off).
 *   2. Keeps person properties fresh — plan / language / has_avatar only.
 *      No PII. signup_provider is set at identify() time (App auth listener).
 *
 * setPersonProperties before identify() is a harmless no-op (person_profiles
 * is "identified_only"); the effect re-runs as plan/avatar load post-login.
 */
export function AnalyticsBridge() {
  const { pathname } = useLocation();
  const { plan } = usePremium();
  const { lang } = useLanguage();
  const { hasAvatar } = useAvatar({ preloadModel: false });

  useEffect(() => {
    capturePageview(pathname);
    // Meta Pixel PageView mirrors the PostHog SPA pageview (dedupes the
    // mount-time call that follows initMetaPixel's own PageView).
    metaPageView(pathname);
    // Only public production marketing pages; never app/private routes.
    void metricoolPageView();
  }, [pathname]);

  useEffect(() => {
    setPersonProperties({ plan, language: lang, has_avatar: hasAvatar });
  }, [plan, lang, hasAvatar]);

  return null;
}
